import { useEffect, useState, useRef } from "react";
import { Send, Calendar, CheckCircle, Headphones, X } from "lucide-react";

const steps = [
  {
    number: 1,
    icon: Send,
    title: "Sie senden uns Ihre Anfrage",
    description: "Teilen Sie uns mit, welche Dienstleistung Sie benötigen – per Formular, E-Mail oder Telefon.",
  },
  {
    number: 2,
    icon: Calendar,
    title: "Wir planen die Umsetzung",
    description: "Wir koordinieren den passenden Dienstleister und stimmen alle Details mit Ihnen ab.",
  },
  {
    number: 3,
    icon: CheckCircle,
    title: "Die Arbeit wird zuverlässig ausgeführt",
    description: "Unsere qualifizierten Partner führen die Arbeiten professionell und termingerecht aus.",
  },
  {
    number: 4,
    icon: Headphones,
    title: "Ein einziger Ansprechpartner",
    description: "Bei Fragen oder Anliegen sind wir jederzeit für Sie da – schnell und unkompliziert.",
  },
];

const JourneyPath = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hideAtEnd, setHideAtEnd] = useState(false);
  const [isPopupDismissed, setIsPopupDismissed] = useState(false);
  const [isMobilePopupDismissed, setIsMobilePopupDismissed] = useState(false);
  const pathRef = useRef<HTMLDivElement>(null);
  const activeStepRef = useRef(0);

  useEffect(() => {
    let frame = 0;
    const updateScroll = () => {
      frame = 0;
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      const rawProgress = scrollHeight > 0 ? currentScroll / scrollHeight : 0;
      const progress = Number.isFinite(rawProgress) ? Math.max(0, Math.min(rawProgress, 1)) : 0;
      
      // Calculate active step based on scroll progress
      const stepProgress = progress * (steps.length + 0.5);
      const newActiveStep = Math.max(0, Math.min(Math.floor(stepProgress), steps.length - 1));
      
      // Reset popup visibility when step changes
      if (newActiveStep !== activeStepRef.current) {
        activeStepRef.current = newActiveStep;
        setActiveStep(newActiveStep);
        setIsPopupDismissed(false);
      }
      
      // Show path after scrolling past hero (roughly 5% of page)
      setIsVisible(progress > 0.05);
      
      // Hide when reaching the contact section (roughly 85% of page)
      setHideAtEnd(progress > 0.85);
    };
    const handleScroll = () => {
      if (!frame) frame = requestAnimationFrame(updateScroll);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    updateScroll();
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);

  // Don't render if at end section
  if (hideAtEnd) return null;

  return (
    <>
      {/* Desktop: Fixed vertical path on right side */}
      <div
        ref={pathRef}
        className={`fixed right-3 xl:right-5 top-1/2 -translate-y-1/2 z-[30] hidden lg:flex flex-col items-center gap-0 transition-opacity duration-700 ${
          isVisible ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {steps.map((step, index) => {
          const Icon = step.icon;
          const opacity = activeStep >= index ? 1 : 0.3;
          const isActive = activeStep >= index;
          const isCurrent = activeStep === index;

          return (
            <div key={step.number} className="flex flex-col items-center">
              {/* Step Node with always-visible content for current step */}
              <div className="relative flex items-center">
                {/* Content popup - shows for current step */}
                <div 
                  className={`absolute right-full mr-3 w-64 transition-all duration-500 ${
                    isCurrent && !isPopupDismissed
                      ? "opacity-100 translate-x-0" 
                      : "opacity-0 translate-x-4 pointer-events-none"
                  }`}
                >
                  <div className="relative glass-card bg-background/90 rounded-2xl p-4 shadow-card border border-border/50 animate-fade-up">
                    {/* Close button */}
                    <button
                      onClick={() => setIsPopupDismissed(true)}
                      className="absolute top-2 right-2 w-5 h-5 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
                      aria-label="Schließen"
                    >
                      <X className="w-3 h-3 text-muted-foreground" />
                    </button>
                    
                    <div className="flex items-center gap-2 mb-2 pr-6">
                      <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                        {step.number}
                      </span>
                      <h4 className="text-sm font-semibold text-foreground">{step.title}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed pl-8">{step.description}</p>
                  </div>
                  {/* Arrow pointing to node */}
                  <div className="absolute top-1/2 -translate-y-1/2 -right-2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-background/95"></div>
                </div>

                {/* Node circle */}
                <div
                  className={`relative flex items-center justify-center w-11 h-11 rounded-2xl border transition-all duration-500 ${
                    isActive
                      ? "gradient-mint border-primary/40 text-primary-foreground shadow-glow"
                      : "bg-card/80 border-border/60 text-muted-foreground shadow-soft"
                  }`}
                  style={{ opacity }}
                >
                  <Icon className="w-5 h-5" />

                  {/* Static current-step highlight avoids continuous repainting. */}
                  {isCurrent && (
                    <div className="absolute inset-0 rounded-2xl ring-2 ring-primary/40"></div>
                  )}
                </div>
              </div>

              {/* Smooth curved walking path between nodes */}
              {index < steps.length - 1 && (
                <div className="h-16 w-12 relative">
                  <svg 
                    className="absolute inset-0 w-full h-full overflow-visible" 
                    viewBox="0 0 48 64"
                    fill="none"
                  >
                    {/* Smooth S-curve path */}
                    <path
                      d="M24 0 C34 8, 36 16, 32 24 C28 32, 18 36, 16 44 C14 52, 18 60, 24 64"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray="4 6"
                      strokeLinecap="round"
                      className={`transition-all duration-500 ${
                        activeStep > index 
                          ? "text-primary" 
                          : "text-muted-foreground/30"
                      }`}
                    />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile/Tablet: Horizontal progress bar at bottom */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 lg:hidden transition-all duration-500 ${
          isVisible && !isMobilePopupDismissed ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"
        }`}
      >
        <div className="relative bg-background/95 border-t border-border/50 px-3 py-2 safe-area-pb">
          {/* Close button for mobile */}
          <button
            onClick={() => setIsMobilePopupDismissed(true)}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors"
            aria-label="Schließen"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          {/* Current step info */}
          <div className="mb-2 px-1 pr-8">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                {steps[activeStep].number}
              </span>
              <h4 className="text-xs font-semibold text-foreground">{steps[activeStep].title}</h4>
            </div>
            <p className="text-[10px] text-muted-foreground leading-snug pl-7 line-clamp-1">
              {steps[activeStep].description}
            </p>
          </div>

          {/* Progress bar */}
          <div className="relative h-1 bg-muted rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary to-primary/70 rounded-full transition-all duration-300"
              style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
          
          {/* Step dots */}
          <div className="flex justify-between items-center mt-1.5 px-1">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = activeStep >= index;
              const isCurrent = activeStep === index;

              return (
                <div
                  key={step.number}
                  className={`flex flex-col items-center transition-all duration-300 ${
                    isActive ? "opacity-100" : "opacity-40"
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground"
                    } ${isCurrent ? "ring-2 ring-primary/50 ring-offset-1 ring-offset-background scale-110" : ""}`}
                  >
                    <Icon className="w-3 h-3" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Spacer for mobile bottom bar */}
      {!isMobilePopupDismissed && <div className="h-20 lg:hidden"></div>}
    </>
  );
};

export default JourneyPath;
